/**
 * Jun 2026 — Google Reviews block for Partner Storefront pages.
 *
 * Pulls the partner's own Google Business Profile reviews (via their
 * configured Place ID) from the cached backend endpoint. Renders
 * nothing at all when the partner has no Place ID, the fetch fails,
 * or Google returns no rating — the storefront never shows an empty
 * block or breaks because of this section.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const GoogleG = ({ className = "w-5 h-5" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Stars = ({ value = 0, size = "w-4 h-4" }) => (
  <div className="flex items-center gap-0.5" aria-label={`${value} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`${size} ${i < Math.round(value) ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
    ))}
  </div>
);

export const GoogleReviewsSection = ({ slug }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/pws-partner-landing/by-slug/${slug}/google-reviews`)
      .then(({ data: d }) => { if (alive && d?.available) setData(d); })
      .catch(() => { /* fail silently — section simply stays hidden */ });
    return () => { alive = false; };
  }, [slug]);

  if (!data) return null;
  const reviews = (data.reviews || []).filter((r) => r.rating);

  return (
    <section className="border-b border-slate-100 py-10 md:py-12 bg-slate-50/60" data-testid="storefront-google-reviews">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <GoogleG />
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Google reviews</h2>
            </div>
            <div className="mt-2 flex items-center gap-2.5" data-testid="google-reviews-summary">
              <span className="text-2xl font-black text-slate-900 tabular-nums">{Number(data.rating).toFixed(1)}</span>
              <Stars value={data.rating} size="w-5 h-5" />
              <span className="text-sm text-slate-500">
                {data.total_review_count ? `${data.total_review_count} Google review${data.total_review_count === 1 ? "" : "s"}` : ""}
              </span>
            </div>
          </div>
          {data.google_maps_uri && (
            <a
              href={data.google_maps_uri}
              target="_blank"
              rel="noreferrer"
              data-testid="google-reviews-view-all"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-cyan-700 hover:text-cyan-900"
            >
              View on Google <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>

        {reviews.length > 0 && (
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reviews.map((r, i) => (
              <motion.div
                key={r.google_maps_uri || i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl bg-white border border-slate-200 p-5 flex flex-col"
                data-testid={`google-review-${i}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <Stars value={r.rating} />
                  {r.relative_date && <span className="text-xs text-slate-400">{r.relative_date}</span>}
                </div>
                {r.text && (
                  <p className="mt-3 text-sm text-slate-700 leading-relaxed flex-1">
                    &ldquo;{r.text}&rdquo;
                  </p>
                )}
                <div className="mt-4 flex items-center gap-2.5">
                  {r.author_photo_url && (
                    <img src={r.author_photo_url} alt="" className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" />
                  )}
                  <div className="text-sm font-semibold text-slate-900">{r.author_name}</div>
                </div>
                {r.google_maps_uri && (
                  <a href={r.google_maps_uri} target="_blank" rel="noreferrer" className="mt-2 text-xs text-slate-500 hover:text-cyan-700">
                    View this review on Google
                  </a>
                )}
              </motion.div>
            ))}
          </div>
        )}

        <p className="mt-6 flex items-center gap-1.5 text-xs text-slate-500" data-testid="google-reviews-attribution">
          <GoogleG className="w-3.5 h-3.5" /> Reviews sourced from Google, shown as returned by Google Maps.
        </p>
      </div>
    </section>
  );
};
